import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { TrendingDown, Percent, PiggyBank, Filter, AlertTriangle } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { SecaoCmv, fmtR$, fmtPct, marginColor } from "../features/estoque-cmv";
import { tamanhosDaPizza, tamanhoDeReferencia, custoVendidoDaPizza } from "../features/estoque-cmv/lib/pizza";
import { CarregandoCards } from "../components/carregando";
import { medidaDoProduto } from "../lib/unidadeVenda";

const API = '/api';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

export default function Reports() {
  const [stats, setStats] = useState(null);
  const [ingredients, setIngredients] = useState([]);
  const [products, setProducts] = useState([]);
  const [salesByProduct, setSalesByProduct] = useState({ total_revenue: 0, by_product: {} });
  const [categoryFilter, setCategoryFilter] = useState("all");
  // Carregamento com cards (25/10/2026): cards e tabela piscam até as 4 buscas
  // voltarem — antes a tela abria com "0.0% · Crítica" e "Nenhum produto".
  const [primeiraCarga, setPrimeiraCarga] = useState(true);

  useEffect(() => {
    Promise.allSettled([
      axios.get(`${API}/reports/dashboard`, auth()).then(r => setStats(r.data)),
      axios.get(`${API}/ingredients`, auth()).then(r => setIngredients(r.data || [])),
      axios.get(`${API}/products`, auth()).then(r => setProducts(r.data || [])),
      axios.get(`${API}/reports/sales-by-product?days=30`, auth())
        .then(r => setSalesByProduct(r.data || { total_revenue: 0, by_product: {} })),
    ]).finally(() => setPrimeiraCarga(false));
  }, []);

  // Produtos elegíveis no relatório: exclui Receitas (são insumos intermediários, não SKU vendido).
  const sellableProducts = useMemo(
    () => products.filter(p => (p.product_type || "produto").toLowerCase() !== "receita"),
    [products]
  );

  // Categorias do filtro saem só dos produtos do relatório: a categoria "Receita"
  // aparecia no filtro e, escolhida, deixava a tela vazia.
  const productCategories = useMemo(() => [...new Set(sellableProducts.map(p => p.category).filter(Boolean))], [sellableProducts]);

  const filteredProducts = useMemo(() => {
    if (categoryFilter === "all") return sellableProducts;
    return sellableProducts.filter(p => p.category === categoryFilter);
  }, [sellableProducts, categoryFilter]);

  // Para "Venda" considera SEMPRE o preço Simples: se for combo (tem simple_price > 0), usa simple_price; senão usa sale_price
  const getVendaSimples = (p) => (p.simple_price && p.simple_price > 0) ? p.simple_price : (p.sale_price || 0);

  // CMV calculado AO VIVO a partir da Ficha Técnica.
  // IMPORTANTE: precisa replicar EXATAMENTE a lógica de Products.js → calculateRecipeCosts,
  // senão o relatório diverge da ficha técnica do produto. Inclui:
  //  - 3 branches por (unit, unit_weight): kg+uw, un+uw, sem-uw
  //  - Fator de perda (loss_percent) — paga pelo bruto, aproveita só (100-loss)%
  const ingredientsById = useMemo(() => {
    const map = {};
    ingredients.forEach(i => { map[i.id] = i; });
    return map;
  }, [ingredients]);

  const computeLiveCmv = (product) => {
    const recipe = product.recipe || [];
    let total = 0;
    recipe.forEach(item => {
      const ing = ingredientsById[item.ingredient_id];
      if (!ing) return;
      const avg = ing.average_price || 0;
      const uw = ing.unit_weight || 0;
      const unit = ing.unit || '';
      const qty = Number(item.quantity) || 0;
      const loss = Number(ing.loss_percent) || 0;
      const lossFactor = (loss > 0 && loss < 100) ? 1 / (1 - loss / 100) : 1;

      let baseCost;
      if (unit === 'kg' && uw > 0) {
        // Ingrediente comprado em kg, mas receita usa unidades (ex: 1 hambúrguer = 0.130 kg)
        baseCost = avg * uw * qty;
      } else if (unit === 'un' && uw > 0) {
        // Ingrediente em unidades, mas receita pesa em kg (ex: 0.020 kg = 20g de tempero)
        baseCost = (avg / uw) * qty;
      } else {
        // Sem unit_weight: quantidade já está na unidade base do estoque
        baseCost = avg * qty;
      }
      total += baseCost * lossFactor;
    });
    return total;
  };

  const productsById = useMemo(() => {
    const map = {};
    products.forEach(p => { map[p.id] = p; });
    return map;
  }, [products]);

  // Preço e custo de UMA unidade. Pizza tem ficha por tamanho (pizza_recipes) e
  // entra pelo tamanho de referência — antes caía na ficha comum, vazia, e
  // toda pizza aparecia "sem ficha técnica" (25/10/2026).
  const precoECusto = (p) => {
    const venda = getVendaSimples(p);
    const tamanhos = tamanhosDaPizza(p, productsById, ingredientsById);
    const ref = tamanhoDeReferencia(tamanhos, venda);
    if (ref) return { venda: ref.preco, cmv: ref.cmv, tamanhos, ref };
    return { venda, cmv: computeLiveCmv(p), tamanhos, ref: null };
  };

  const metrics = useMemo(() => {
    // Considera apenas produtos com FICHA TÉCNICA COMPLETA — i.e., margem < 100%.
    // Produtos sem ficha (CMV=0) gerariam margin=100%, inflando artificialmente as médias.
    const prods = filteredProducts
      .map(p => {
        const { venda, cmv } = precoECusto(p);
        const margin = venda > 0 ? ((venda - cmv) / venda) * 100 : 0;
        return { p, venda, cmv, margin };
      })
      .filter(x => x.venda > 0 && x.cmv > 0 && x.margin < 100);
    if (!prods.length) return { avgSalePrice: 0, avgCost: 0, avgProfit: 0, avgCMV: 0, avgMargin: 0, semFicha: true };
    const avgSalePrice = prods.reduce((s, x) => s + x.venda, 0) / prods.length;
    const avgCost = prods.reduce((s, x) => s + x.cmv, 0) / prods.length;
    const avgCMV = prods.reduce((s, x) => s + (x.cmv / x.venda) * 100, 0) / prods.length;
    const avgMargin = prods.reduce((s, x) => s + x.margin, 0) / prods.length;
    return { avgSalePrice, avgCost, avgProfit: avgSalePrice - avgCost, avgCMV, avgMargin };
  }, [filteredProducts, ingredientsById, productsById]);

  // Linhas da seção de CMV (features/estoque-cmv): busca, ordem e agrupamento moram lá.
  const cmvRows = useMemo(() => {
    const totalRev = salesByProduct.total_revenue || 0;
    const byProduct = salesByProduct.by_product || {};
    return filteredProducts.map(p => {
      const { venda, cmv, tamanhos, ref } = precoECusto(p);
      const lucro = venda - cmv;
      const margin = venda > 0 ? (lucro / venda) * 100 : 0;
      const cmvPct = venda > 0 ? (cmv / venda) * 100 : 0;
      const soldData = byProduct[p.id] || {};
      const soldRevenue = Number(soldData.revenue) || 0;
      const soldCount = Number(soldData.sold_count) || 0;
      const salesShare = totalRev > 0 ? (soldRevenue / totalRev) * 100 : 0;
      return {
        id: p.id, name: ref ? `${p.name} · ${ref.nome}` : p.name, category: p.category, photo_url: p.photo_url,
        cmv, cmvPct, venda, lucro, margin,
        soldRevenue, soldCount, salesShare,
        // Pizza: cada tamanho vendido pelo custo dele (o backend conta por tamanho)
        ...(ref ? { custoVendido: custoVendidoDaPizza(tamanhos, ref, soldData.por_tamanho, soldCount) } : {}),
        // Quilo/litro (30/09/2026): soldCount é em kg/L nesse produto.
        unidade: soldData.unidade !== undefined ? soldData.unidade : medidaDoProduto(p),
      };
    }).filter(p => p.venda > 0 || p.cmv > 0);
  }, [filteredProducts, ingredientsById, productsById, salesByProduct]);

  // Top 5 produtos com pior margem (atenção)
  const piorMargem = useMemo(() => {
    return [...cmvRows]
      .filter(p => p.venda > 0 && p.cmv > 0)
      .sort((a, b) => a.margin - b.margin)
      .slice(0, 5);
  }, [cmvRows]);


  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold">Relatório de Estoque</h1>
          <p className="text-muted-foreground mt-1">Custos, preços e margens</p>
        </div>
        <div className="flex items-center gap-3">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[190px] h-9"><SelectValue placeholder="Todas categorias" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas as categorias</SelectItem>
              {productCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          {stats && (
            <span className="text-xs text-muted-foreground hidden md:inline">
              {stats.total_products} produtos · {stats.total_ingredients} ingredientes · {stats.total_purchases} compras
            </span>
          )}
        </div>
      </div>

      {/* SEÇÃO 1: KPIs principais */}
      {primeiraCarga ? (
        <CarregandoCards quantos={3} className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5" altura="h-[148px]" />
      ) : (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
        {/* Margem (KPI principal) */}
        <div className={`relative rounded-2xl border p-5 overflow-hidden ${
          metrics.semFicha ? 'bg-card' :
          metrics.avgMargin >= 50 ? 'bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border-emerald-500/40' :
          metrics.avgMargin >= 30 ? 'bg-gradient-to-br from-green-500/10 to-green-500/5 border-green-500/40' :
          metrics.avgMargin >= 15 ? 'bg-gradient-to-br from-amber-500/10 to-amber-500/5 border-amber-500/40' :
          'bg-gradient-to-br from-red-500/10 to-red-500/5 border-red-500/40'
        }`}>
          <div className="flex items-start justify-between mb-2">
            <div>
              <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Margem média</p>
              <p className="text-[10px] text-muted-foreground/70 mt-0.5">do cardápio</p>
            </div>
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${
              metrics.semFicha ? 'bg-muted text-muted-foreground' :
              metrics.avgMargin >= 30 ? 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400' :
              metrics.avgMargin >= 15 ? 'bg-amber-500/20 text-amber-600 dark:text-amber-400' :
              'bg-red-500/20 text-red-600 dark:text-red-400'
            }`}>
              <Percent className="w-5 h-5" />
            </div>
          </div>
          <p className={`text-4xl font-black tabular-nums tracking-tight ${
            metrics.semFicha ? 'text-muted-foreground' :
            metrics.avgMargin >= 30 ? 'text-emerald-600 dark:text-emerald-400' :
            metrics.avgMargin >= 15 ? 'text-amber-600 dark:text-amber-400' :
            'text-red-600 dark:text-red-400'
          }`}>{metrics.semFicha ? '—' : fmtPct(metrics.avgMargin)}</p>
          <p className="text-[10px] text-muted-foreground mt-2">
            {metrics.semFicha ? 'Cadastre a ficha técnica dos produtos para ver a margem' :
             metrics.avgMargin >= 50 ? '✅ Excelente — saudável' :
             metrics.avgMargin >= 30 ? '👌 Boa — dentro do esperado' :
             metrics.avgMargin >= 15 ? '⚠️ Apertada — atenção' :
             '🚨 Crítica — revise preços'}
          </p>
        </div>

        {/* Lucro Médio */}
        <div className="rounded-2xl border bg-card p-5">
          <div className="flex items-start justify-between mb-2">
            <div>
              <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Lucro médio</p>
              <p className="text-[10px] text-muted-foreground/70 mt-0.5">venda − custo, por item</p>
            </div>
            <div className="w-9 h-9 rounded-xl bg-emerald-500/15 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
              <PiggyBank className="w-5 h-5" />
            </div>
          </div>
          <p className={`text-3xl font-black tabular-nums tracking-tight ${metrics.semFicha ? 'text-muted-foreground' : metrics.avgProfit >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
            {metrics.semFicha ? '—' : fmtR$(metrics.avgProfit)}
          </p>
          <div className="flex items-center gap-3 mt-2 text-[10px] text-muted-foreground">
            {metrics.semFicha ? <span>Sem produto com ficha técnica</span> : (<>
            <span>Venda <strong className="text-foreground tabular-nums">{fmtR$(metrics.avgSalePrice)}</strong></span>
            <span className="text-border">·</span>
            <span>Custo <strong className="text-red-600 dark:text-red-400 tabular-nums">{fmtR$(metrics.avgCost)}</strong></span>
            </>)}
          </div>
        </div>

        {/* CMV Médio */}
        <div className="rounded-2xl border bg-card p-5">
          <div className="flex items-start justify-between mb-2">
            <div>
              <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">CMV médio</p>
              <p className="text-[10px] text-muted-foreground/70 mt-0.5">% custo sobre venda</p>
            </div>
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${
              metrics.avgCMV <= 30 ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' :
              metrics.avgCMV <= 40 ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400' :
              'bg-red-500/15 text-red-600 dark:text-red-400'
            }`}>
              <TrendingDown className="w-5 h-5" />
            </div>
          </div>
          <p className={`text-3xl font-black tabular-nums tracking-tight ${
            metrics.semFicha ? 'text-muted-foreground' :
            metrics.avgCMV <= 30 ? 'text-emerald-600 dark:text-emerald-400' :
            metrics.avgCMV <= 40 ? 'text-amber-600 dark:text-amber-400' :
            'text-red-600 dark:text-red-400'
          }`}>{metrics.semFicha ? '—' : fmtPct(metrics.avgCMV)}</p>
          <p className="text-[10px] text-muted-foreground mt-2">
            {metrics.semFicha ? 'Sem produto com ficha técnica' :
             metrics.avgCMV <= 30 ? 'Saudável (até 30%)' :
             metrics.avgCMV <= 40 ? 'Atenção (30-40%)' :
             'Alto (acima de 40%)'}
          </p>
        </div>
      </div>
      )}

      {/* SEÇÃO 2: Atenção — produtos com pior margem */}
      {piorMargem.length > 0 && piorMargem[0].margin < 30 && (
        <div className="bg-gradient-to-r from-amber-50/80 to-red-50/40 dark:from-amber-950/30 dark:to-red-950/10 border border-amber-300 dark:border-amber-800 rounded-2xl p-4 mb-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
              <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-amber-800 dark:text-amber-300">Produtos com margem baixa</h3>
              <p className="text-[11px] text-amber-700/80 dark:text-amber-400/80">Considere ajustar o preço ou rever a ficha técnica</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-2">
            {piorMargem.filter(p => p.margin < 30).slice(0, 5).map(p => (
              <div key={p.id} className="bg-card rounded-lg border border-amber-200 dark:border-amber-900/50 p-2.5">
                <p className="text-xs font-semibold truncate" title={p.name}>{p.name}</p>
                <div className="flex items-center justify-between mt-1.5 text-[11px]">
                  <span className="text-muted-foreground tabular-nums">{fmtR$(p.venda)}</span>
                  <span className={`font-mono font-bold ${marginColor(p.margin)}`}>{p.margin.toFixed(2).replace('.', ',')}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SEÇÃO 3: CMV por categoria (abre para os produtos) ou por produto */}
      <SecaoCmv linhas={cmvRows} carregando={primeiraCarga} />
    </div>
  );
}
